/** Chatbase's loader is available before its lazy conversation iframe is usable. */
export type ChatbaseStatus = 'loading' | 'available' | 'opening' | 'ready' | 'unavailable';

export interface ChatbaseSnapshot {
    status: ChatbaseStatus;
    fallbackVisible: boolean;
    error?: Error;
}

export const CHATBASE_OPEN_TIMEOUT_MS = 20000;
const ORIGIN = 'https://www.chatbase.co';
const CONTAINER_SELECTOR = '#chatbase-bubble-window, .chatbase-bubble-window, #chatbase-chat-window, .chatbase-chat-window';
const FAILED_CLASS = 'tempstars-chatbase-unavailable';
const FALLBACK_CLASS = 'tempstars-chatbase-fallback-visible';
const INACTIVE_CLASS = 'tempstars-chatbase-inactive';
const listeners = new Set<(snapshot: ChatbaseSnapshot) => void>();
let snapshot: ChatbaseSnapshot = { status: 'loading', fallbackVisible: false };
let readyFrame: HTMLIFrameElement | null = null;
let readySource = '';
let timeout: ReturnType<typeof setTimeout> | undefined;
let poll: ReturnType<typeof setInterval> | undefined;
let clickTimer: ReturnType<typeof setTimeout> | undefined;
let opened = false;
let contactSupportRequested = false;
let actor: string | undefined;
let resetPending = false;
let resetFrame: HTMLIFrameElement | null = null;
let resetSource = '';
let needsRemountReset = false;

export const requestChatbaseSupport = () => { contactSupportRequested = true; };
export const consumeChatbaseSupportRequest = () => {
    const requested = contactSupportRequested;
    contactSupportRequested = false;
    return requested;
};

const currentFrame = (): HTMLIFrameElement | null => {
    const frame = document.querySelector<HTMLIFrameElement>(
        '#chatbase-bubble-window iframe, #chatbase-chat-window iframe',
    );
    if (!frame?.isConnected) return null;
    try {
        const url = new URL(frame.src);
        return url.origin === ORIGIN && url.pathname.startsWith('/chatbot-iframe/') ? frame : null;
    } catch {
        return null;
    }
};

const isOpen = () => {
    const container = document.querySelector<HTMLElement>(CONTAINER_SELECTOR);
    return !!container && window.getComputedStyle(container).display !== 'none';
};

const publish = (next: ChatbaseSnapshot) => {
    snapshot = next;
    document.body.classList.toggle(FAILED_CLASS, next.status === 'unavailable');
    document.body.classList.toggle(FALLBACK_CLASS, next.fallbackVisible);
    listeners.forEach(listener => listener(snapshot));
};

const clearAttempt = () => {
    clearTimeout(timeout);
    clearInterval(poll);
    timeout = undefined;
    poll = undefined;
    opened = false;
};

export const closeChatbaseConversation = () => {
    try {
        window.chatbase?.close?.();
    } catch {
        // The unavailable CSS also hides a broken vendor window.
    }
    document.querySelectorAll<HTMLElement>(CONTAINER_SELECTOR).forEach(container => {
        container.style.display = 'none';
    });
    document.documentElement.classList.remove('__cb_widget_active');
    window.dispatchEvent(new CustomEvent('chatbase:closed'));
};

export const dismissChatbaseFallback = () => {
    publish({ ...snapshot, fallbackVisible: false });
};

export const getChatbaseSnapshot = () => snapshot;

export const failChatbase = (error: Error, showFallback = snapshot.status === 'opening') => {
    clearAttempt();
    publish({ status: 'unavailable', error, fallbackVisible: showFallback });
    closeChatbaseConversation();
};

export const markChatbaseAvailable = () => {
    // Preserve a conversation failure when navigating Home -> Support.
    if (snapshot.status === 'loading') publish({ status: 'available', fallbackVisible: false });
};

const cancelOpening = () => {
    clearAttempt();
    if (snapshot.status === 'opening') publish({ status: 'available', fallbackVisible: false });
};

const resetConversation = (frame: HTMLIFrameElement) => {
    resetFrame = frame;
    resetSource = frame.src;
    try {
        if (typeof window.chatbase?.resetChat !== 'function') throw new Error('Chatbase reset unavailable');
        window.chatbase.resetChat();
    } catch (error) {
        failChatbase(error instanceof Error ? error : new Error('Unable to reset Chatbase'));
    }
};

/** Legacy logout does not reload the document. Never reuse another actor's chat. */
export const setChatbaseActor = (identity: string | undefined) => {
    if (!identity || identity === actor) return;
    actor = identity;
    const frame = currentFrame();
    const previouslyReady = frame && frame === readyFrame && frame.src === readySource;
    readyFrame = null;
    readySource = '';
    resetFrame = null;
    resetSource = '';
    resetPending = true;
    document.body.classList.add('tempstars-chatbase-resetting');
    cancelOpening();
    closeChatbaseConversation();
    if (snapshot.status === 'ready') publish({ status: 'available', fallbackVisible: false });
    // resetChat is not queued by the vendor's lazy Proxy. For a fresh iframe,
    // wait for its first conversationReady before resetting and accepting the
    // next readiness signal. Keep old content hidden throughout that handshake.
    if (previouslyReady && snapshot.status !== 'unavailable') resetConversation(frame);
};

const beginOpening = () => {
    const frame = currentFrame();
    if (snapshot.status === 'ready' && frame === readyFrame && frame?.src === readySource) return;
    if (snapshot.status === 'opening') return;
    readyFrame = null;
    readySource = '';
    opened = isOpen();
    publish({ status: 'opening', fallbackVisible: false });
    timeout = setTimeout(() => {
        if (opened && !isOpen()) {
            cancelOpening();
            return;
        }
        failChatbase(new Error('Chatbase conversation did not become ready'));
    }, CHATBASE_OPEN_TIMEOUT_MS);
    // Bounded to an opening attempt. No observer of the app's body/subtree.
    poll = setInterval(() => {
        if (isOpen()) opened = true;
        else if (opened) cancelOpening();
    }, 250);
};

/** Use for our buttons; assigning chatbase.open is ineffective on the vendor Proxy. */
export const openChatbaseWidget = (): boolean => {
    if (listeners.size === 0) return false;
    if (document.body.classList.contains('tempstars-chatbase-ui-hidden')) return false;
    if (snapshot.status === 'unavailable') {
        publish({ ...snapshot, fallbackVisible: true });
        return true;
    }
    if (typeof window.chatbase?.open !== 'function') return false;
    beginOpening();
    try {
        window.chatbase.open();
        window.dispatchEvent(new CustomEvent('chatbase:open'));
    } catch (error) {
        failChatbase(error instanceof Error ? error : new Error('Unable to open Chatbase'), true);
    }
    return true;
};

const onMessage = (event: MessageEvent) => {
    // conversationReady is an internal vendor signal; isolate it here and fail
    // visibly on timeout if the vendor changes it. iframeReady/optionsChanged
    // only describe initialization, not a rendered conversation.
    if (event.origin !== ORIGIN || !event.data || typeof event.data !== 'object'
        || Array.isArray(event.data)) return;
    const frame = currentFrame();
    if (!frame || event.source !== frame.contentWindow) return;
    if (event.data.type === 'close') {
        cancelOpening();
        window.dispatchEvent(new CustomEvent('chatbase:closed'));
        return;
    }
    if (event.data.type !== 'conversationReady' || snapshot.status === 'unavailable') return;
    if (resetPending) {
        if (resetFrame !== frame || resetSource !== frame.src) {
            resetConversation(frame);
            return;
        }
        resetPending = false;
        resetFrame = null;
        resetSource = '';
        document.body.classList.remove('tempstars-chatbase-resetting');
    }
    if (snapshot.status === 'ready' && frame === readyFrame && frame.src === readySource) return;
    readyFrame = frame;
    readySource = frame.src;
    clearAttempt();
    publish({ status: 'ready', fallbackVisible: false });
};

const onVendorClick = (event: MouseEvent) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target?.closest('#chatbase-bubble-button, #chatbase-message-bubbles')) return;
    // Dismissing a proactive message must never count as launching chat.
    if (target.closest('#chatbase-message-bubbles button')) return;
    if (snapshot.status === 'unavailable') {
        event.preventDefault();
        event.stopPropagation();
        publish({ ...snapshot, fallbackVisible: true });
        return;
    }
    clearTimeout(clickTimer);
    clickTimer = setTimeout(() => {
        if (isOpen()) {
            beginOpening();
            window.dispatchEvent(new CustomEvent('chatbase:open'));
        } else {
            cancelOpening();
            window.dispatchEvent(new CustomEvent('chatbase:closed'));
        }
    }, 0);
};

export const subscribeChatbase = (listener: (state: ChatbaseSnapshot) => void) => {
    if (listeners.size === 0) {
        document.body.classList.remove(INACTIVE_CLASS);
        window.addEventListener('message', onMessage);
        window.addEventListener('chatbase:closed', cancelOpening);
        document.addEventListener('click', onVendorClick, true);
        const frame = currentFrame();
        if (snapshot.status === 'ready' && (frame !== readyFrame || frame?.src !== readySource)) {
            snapshot = { status: 'available', fallbackVisible: false };
        }
        if (needsRemountReset && snapshot.status !== 'unavailable') {
            needsRemountReset = false;
            resetPending = true;
            resetFrame = null;
            resetSource = '';
            document.body.classList.add('tempstars-chatbase-resetting');
            // The SDK can finish while this route has no owner/listener. Its
            // inline visibility signals a functioning iframe, not our readiness.
            // Ask that frame for a fresh reset/ready handshake instead of waiting
            // forever for the event we missed while the launcher was hidden.
            if (frame?.style.visibility === 'visible') resetConversation(frame);
        }
    }
    listeners.add(listener);
    listener(snapshot);
    return () => {
        listeners.delete(listener);
        if (listeners.size === 0) {
            needsRemountReset = resetPending || snapshot.status === 'opening';
            // The persistent SDK keeps its launcher after close(). Hide it on
            // routes with no owner able to render a monitored support fallback.
            document.body.classList.add(INACTIVE_CLASS);
            window.removeEventListener('message', onMessage);
            window.removeEventListener('chatbase:closed', cancelOpening);
            document.removeEventListener('click', onVendorClick, true);
            clearTimeout(clickTimer);
            cancelOpening();
            dismissChatbaseFallback();
            closeChatbaseConversation();
        }
    };
};
